import { IconName, notEmpty } from '@talos/kyoko';
import { useMemo } from 'react';
import { PANEL_COMPONENTS_MAP } from '../../../components/AppLayout/Panels';
import { BLOTTER_PANEL_COMPONENTS } from '../../../components/AppLayout/Panels/Trading/blotters/blotters';
import { getRemoveTabMessage } from '../../../components/Layout/utils/utils';
import { useAppLayoutForRouteParams } from '../../../hooks/useAppLayoutId';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { getLayoutGroupById } from '../../../providers/AppLayoutContextProvider/utils/getLayoutGroupById';
import type { NavigationItem } from '../../../tokens/navigationPaths';
import type { LayoutSpec, PanelType } from '../../../types/LayoutConfig';
import { useCurrentUserOpenOrdersCount } from '../../Blotters/Orders/useOpenOrdersCount';
import { getTradingLayoutBasePath, getTradingPanelRoute } from './Blotters/navRoutes';
import { isEnabledMarketTabPanelType } from './Markets/marketTabPanelTypes';
import { MOBILE_SUPPORTED_PANEL_TYPES } from './utils/mobileSupportedPanelTypes';

const BLOTTER_SORT_POSITIONS = new Map(BLOTTER_PANEL_COMPONENTS.map((panel, index) => [panel.type, index]));
const BLOTTER_OVERRIDES: Partial<Record<PanelType, { position?: number; icon: IconName }>> = {
  ordersBlotter: { position: -2, icon: IconName.Orders },
  positionsBlotter: { position: -1, icon: IconName.Positions },
};

export function useMobileTradingNavItems() {
  const currentLayout = useAppLayoutForRouteParams('trading');
  const currentUserOpenOrdersCount = useCurrentUserOpenOrdersCount();
  const { enablePredictionMarkets } = useFeatureFlag();

  const marketTabs: NavigationItem[] = useMemo(() => {
    if (currentLayout == null) {
      return [];
    }

    const marketPanels = getMarketTabsForLayout(currentLayout, enablePredictionMarkets);
    return marketPanels.map(panel => {
      const panelType = panel.contentComponent ?? panel.params?.type;
      const label = panel.title;
      return {
        id: panel.id,
        label,
        path: getTradingPanelRoute(currentLayout, panel.id),
        isEditable: true,
        isRemovable: marketPanels.length > 1,
        removeConfirmationText: getRemoveTabMessage(label ?? 'this', panelType ?? ''),
      };
    });
  }, [currentLayout, enablePredictionMarkets]);

  const blotterPanels = useMemo<NavigationItem[]>(() => {
    if (currentLayout == null) {
      return [];
    }

    const sortedBlotterPanels = Object.values(currentLayout.dockViewLayout.panels)
      .map((panel, index) => {
        const panelType = panel.params?.type as PanelType;
        const component = PANEL_COMPONENTS_MAP[panelType];

        if (component?.group?.type !== 'blotters' || !MOBILE_SUPPORTED_PANEL_TYPES.includes(panelType)) {
          return undefined;
        }

        const path = getTradingPanelRoute(currentLayout, panel.id);
        return {
          item: {
            id: panel.id,
            label: panel.title,
            icon: (panelType && BLOTTER_OVERRIDES[panelType]?.icon) || component.icon,
            indicatorCount: panelType === 'ordersBlotter' ? currentUserOpenOrdersCount : undefined,
            path,
            // Wildcard child so the TabBar matches the blotter when a specific tab (e.g. `/ordersBlotter/open-orders`)
            // is active. The actual tab routing is handled by MobileTradingPanelDispatcher.
            children: [{ path: `${path}/*`, label: undefined }],
          },
          position: panelType
            ? (BLOTTER_OVERRIDES[panelType]?.position ??
              BLOTTER_SORT_POSITIONS.get(panelType) ??
              Number.MAX_SAFE_INTEGER)
            : Number.MAX_SAFE_INTEGER,
          index,
        };
      })
      .filter(notEmpty)
      .toSorted((a, b) => {
        if (a.position !== b.position) {
          return a.position - b.position;
        }
        return a.index - b.index;
      });

    return sortedBlotterPanels.map(({ item }) => item);
  }, [currentLayout, currentUserOpenOrdersCount]);

  const marketPath = currentLayout ? getTradingLayoutBasePath(currentLayout) : '/trading';
  const activePanelId = currentLayout ? getActivePanelId(currentLayout) : undefined;
  const marketNavigationPath = (marketTabs.find(tab => tab.id === activePanelId) ?? marketTabs[0])?.path;

  return [
    {
      id: 'market',
      label: 'Market',
      path: marketPath,
      // Preserve layout-root matching while bypassing active-panel redirects on explicit Market clicks.
      navigationPath: marketNavigationPath,
      icon: IconName.Market,
      children: marketTabs,
    },
    ...blotterPanels,
  ];
}

function getMarketTabsForLayout(layout: LayoutSpec, enablePredictionMarkets: boolean) {
  const allPanels = Object.values(layout.dockViewLayout.panels);
  return allPanels.filter(panel => isEnabledMarketTabPanelType(panel.contentComponent, enablePredictionMarkets));
}

function getActivePanelId(layout: LayoutSpec) {
  const activeGroupId = (layout.dockViewLayout as LayoutSpec['dockViewLayout'] & { activeGroup?: string }).activeGroup;
  return activeGroupId ? getLayoutGroupById(layout.dockViewLayout, activeGroupId)?.data.activeView : undefined;
}
